/**
 * Service layer for the workflow execution-tracking console. Thin typed
 * wrappers over `workflowRunsClient` + `endpoints.workflowRuns`. Reads return
 * DTOs directly (no mapper — wire shapes are already UI-ready); control POSTs
 * return the uniform `ControlActionResponse`. All calls are tenant-scoped by
 * the JWT `subsidiaryId` claim injected by the client's interceptors.
 */
import { workflowRunsClient } from '~/api/client';
import { endpoints } from '~/api/endpoints';

import type {
  ControlActionRequest,
  ControlActionResponse,
  ControlHistoryEntry,
  GateDecisionRequest,
  Lineage,
  LiveProgress,
  ParityReport,
  ProductJourney,
  ProductsPage,
  ProductsQuery,
  RetryRequest,
  RunDetail,
  RunListFilters,
  RunSummary,
  StageDrilldown,
  TreeNode,
} from '../types/workflow-runs.types';

// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------

/** List FLOW-level runs for the active tenant, optionally filtered. */
export async function listWorkflowRuns(filters: RunListFilters = {}): Promise<RunSummary[]> {
  const searchParams: Record<string, string> = {};
  if (filters.flowType) searchParams.flowType = filters.flowType;
  if (filters.status) searchParams.status = filters.status;
  return workflowRunsClient
    .get(endpoints.workflowRuns.list(), { searchParams })
    .json<RunSummary[]>();
}

/** Run detail — reconciled summary header + stage checklist. */
export async function getWorkflowRunDetail(runId: string): Promise<RunDetail> {
  return workflowRunsClient.get(endpoints.workflowRuns.detail(runId)).json<RunDetail>();
}

/** Live "N of M" counter for the current stage — poll while `source==LIVE`. */
export async function getLiveProgress(runId: string): Promise<LiveProgress> {
  return workflowRunsClient.get(endpoints.workflowRuns.liveProgress(runId)).json<LiveProgress>();
}

/** Stage drill-down — the batch / sub-batch child nodes for one stage. */
export async function getStageDrilldown(runId: string, stage: string): Promise<StageDrilldown> {
  return workflowRunsClient.get(endpoints.workflowRuns.stage(runId, stage)).json<StageDrilldown>();
}

/** Full nested execution tree (FLOW → COORDINATOR → BATCH → SUB_BATCH). */
export async function getExecutionTree(runId: string): Promise<TreeNode> {
  return workflowRunsClient.get(endpoints.workflowRuns.tree(runId)).json<TreeNode>();
}

/**
 * One keyset page of the per-product ledger. `stage` is always sent (the
 * service 400s without it despite the OpenAPI marking it optional). `cursor` is
 * opaque — pass the previous page's `nextCursor` back verbatim.
 */
export async function getProductsPage(
  runId: string,
  query: ProductsQuery,
  cursor?: string,
): Promise<ProductsPage> {
  const searchParams: Record<string, string | number> = { stage: query.stage };
  if (query.status) searchParams.status = query.status;
  if (query.batchExecutionId) searchParams.batchExecutionId = query.batchExecutionId;
  if (query.size) searchParams.size = query.size;
  if (cursor) searchParams.cursor = cursor;
  return workflowRunsClient
    .get(endpoints.workflowRuns.products(runId), { searchParams })
    .json<ProductsPage>();
}

/** One product's journey — every attempt across stages + provenance. */
export async function getProductJourney(runId: string, productId: string): Promise<ProductJourney> {
  return workflowRunsClient
    .get(endpoints.workflowRuns.productJourney(runId, productId))
    .json<ProductJourney>();
}

/** Data-lineage graph — assets (nodes) + links (edges). */
export async function getLineage(runId: string): Promise<Lineage> {
  return workflowRunsClient.get(endpoints.workflowRuns.lineage(runId)).json<Lineage>();
}

/** Control + lifecycle audit timeline (ascending by `occurredAt`). */
export async function getControlHistory(runId: string): Promise<ControlHistoryEntry[]> {
  return workflowRunsClient
    .get(endpoints.workflowRuns.controlHistory(runId))
    .json<ControlHistoryEntry[]>();
}

/** Shadow-parity report (ops/QA cutover gate). */
export async function getParity(runId: string): Promise<ParityReport> {
  return workflowRunsClient.get(endpoints.workflowRuns.parity(runId)).json<ParityReport>();
}

// ---------------------------------------------------------------------------
// Controls — act on the live Temporal workflow
// ---------------------------------------------------------------------------

/** Hold the run at the next stage boundary. */
export async function pauseWorkflowRun(
  runId: string,
  body: ControlActionRequest = {},
): Promise<ControlActionResponse> {
  return workflowRunsClient
    .post(endpoints.workflowRuns.pause(runId), { json: body })
    .json<ControlActionResponse>();
}

/** Release a paused run. */
export async function resumeWorkflowRun(
  runId: string,
  body: ControlActionRequest = {},
): Promise<ControlActionResponse> {
  return workflowRunsClient
    .post(endpoints.workflowRuns.resume(runId), { json: body })
    .json<ControlActionResponse>();
}

/** Force-close a run. */
export async function terminateWorkflowRun(
  runId: string,
  body: ControlActionRequest = {},
): Promise<ControlActionResponse> {
  return workflowRunsClient
    .post(endpoints.workflowRuns.terminate(runId), { json: body })
    .json<ControlActionResponse>();
}

/** Decide a stage that is AWAITING_GATE (approve / reject / jump-back). */
export async function decideGate(
  runId: string,
  stage: string,
  body: GateDecisionRequest,
): Promise<ControlActionResponse> {
  return workflowRunsClient
    .post(endpoints.workflowRuns.gate(runId, stage), { json: body })
    .json<ControlActionResponse>();
}

/** Retry a run — scope STAGE (reset & replay) or FAILED_ITEMS (re-run products). */
export async function retryWorkflowRun(
  runId: string,
  body: RetryRequest,
): Promise<ControlActionResponse> {
  return workflowRunsClient
    .post(endpoints.workflowRuns.retry(runId), { json: body })
    .json<ControlActionResponse>();
}
